import { useState } from "react";
import type { TournamentRole } from "@alpha/shared";
import { api, apiErrorMessage } from "../../../lib/api";
import { useTournamentRoleMutation, useTournamentRoles } from "../../../lib/adminData";
import type { TournamentRoleGrant } from "../../../lib/types";
import { Badge, Button, Input, SectionCard } from "../../../components/ui";

const STAFF_ROLE_LABEL: Record<TournamentRole, string> = {
  tournamentAdmin: "Tournament admin",
  tournamentScorer: "Scorer",
};

const staffLabel = (grant: TournamentRoleGrant): string => grant.email ?? grant.name ?? grant.uid;

function StaffRow({ tid, grant }: { tid: string; grant: TournamentRoleGrant }) {
  const [error, setError] = useState<string>();
  const label = staffLabel(grant);
  const revoke = useTournamentRoleMutation(tid, () =>
    api.del(`/admin/tournaments/${tid}/roles/${grant.uid}`),
  );
  const canRevoke = grant.role === "tournamentScorer";

  return (
    <div className="flex flex-wrap items-center justify-between gap-3 rounded-xl border border-slate-200 bg-white p-3 text-sm">
      <div className="min-w-0">
        <p className="truncate font-medium text-navy-950">{label}</p>
        {grant.name && grant.name !== label ? (
          <p className="truncate text-xs text-slate-500">{grant.name}</p>
        ) : null}
      </div>
      <div className="flex shrink-0 items-center gap-2">
        <Badge tone="slate">{STAFF_ROLE_LABEL[grant.role]}</Badge>
        {canRevoke ? (
          <Button
            size="sm"
            variant="ghost"
            aria-label={`Revoke scorer access for ${label}`}
            disabled={revoke.isPending}
            onClick={() =>
              revoke.mutate(undefined, {
                onError: (e) => setError(apiErrorMessage(e, "Could not revoke this scorer.")),
              })
            }
          >
            Revoke
          </Button>
        ) : null}
      </div>
      {error ? <p className="basis-full text-sm text-red-600">{error}</p> : null}
    </div>
  );
}

/**
 * Who can act on this event besides the director. Granting is by email — that
 * is what an organizer knows — and the API will only write a scorer from here.
 * Tournament-admin grants stay on the organization Users page.
 */
export function Staff({ tid }: { tid: string }) {
  const rolesQ = useTournamentRoles(tid);
  const [email, setEmail] = useState("");
  const [error, setError] = useState<string>();
  const grant = useTournamentRoleMutation(tid, () =>
    api.post(`/admin/tournaments/${tid}/roles`, {
      email: email.trim().toLowerCase(),
      role: "tournamentScorer",
    }),
  );
  const grants = rolesQ.data?.grants ?? [];

  return (
    <SectionCard
      title="Staff"
      description="Scorers can enter results on this event. Only an organization admin can add or remove a tournament director."
      badge={grants.length > 0 ? <Badge>{grants.length}</Badge> : undefined}
    >
      {rolesQ.isLoading ? (
        <p className="text-sm text-slate-400">Loading staff…</p>
      ) : rolesQ.isError ? (
        <p className="text-sm text-red-600">Couldn&rsquo;t load staff. Check your connection and try again.</p>
      ) : grants.length === 0 ? (
        <p className="text-sm text-slate-400">No staff yet — grant a scorer below.</p>
      ) : (
        <div className="space-y-2">
          {grants.map((g) => (
            <StaffRow key={g.uid} tid={tid} grant={g} />
          ))}
        </div>
      )}
      <form
        className="flex flex-wrap gap-2 border-t border-slate-100 pt-4"
        onSubmit={(e) => {
          e.preventDefault();
          if (!email.trim()) return;
          grant.mutate(undefined, {
            onSuccess: () => {
              setEmail("");
              setError(undefined);
            },
            onError: (err) => setError(apiErrorMessage(err, "Could not grant this scorer.")),
          });
        }}
      >
        <Input
          className="w-full sm:max-w-64"
          type="email"
          placeholder="scorer@example.com"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          aria-label="Scorer email"
        />
        <Button type="submit" disabled={!email.trim() || grant.isPending}>
          {grant.isPending ? "Saving…" : "Grant scorer"}
        </Button>
        {error ? <p className="basis-full text-sm text-red-600">{error}</p> : null}
      </form>
    </SectionCard>
  );
}
